import React, { useEffect, useState, useContext, useRef } from 'react'
import { CCard, CButton, CFormSelect, CFormInput } from '@coreui/react'
import SearchTable from 'src/components/searchTable'
import moment from 'moment'
import { ANALYTICS_API, TRANSACTION_CAPTURE } from 'src/views/utils/urls'
import useAxios from 'axios-hooks'
import { AppContext } from 'src/views/context/app'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import { isEmpty } from 'lodash'
import { PAGE_SIZE } from 'src/views/utils/constants'
import getSymbolFromCurrency from 'currency-symbol-map'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleDown } from '@fortawesome/free-solid-svg-icons'
import CIcon from '@coreui/icons-react'
import { cilDollar, cilInfo } from '@coreui/icons'
import { raiseToast } from '../../../utils/helper'

const TransactionReport = ({ subscriptionData }) => {
  const dateRangeRef = useRef(null)
  const { setLoader } = useContext(AppContext)
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [subscriptionFilter, setSubscriptionFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('complete')
  const [page, setPage] = useState(1)
  const [transactionList, setTransactionList] = useState([])

  const [{ data: transactionData, loading: transactionDataLoading }, transactionDataCall] =
    useAxios(
      {
        url: `${ANALYTICS_API}/transaction-analytics?page_size=${PAGE_SIZE}&order_by=modified_date`,
        method: 'get',
      },
      { manual: true },
    )

  const [{ data: captureTransactionData, loading: captureTransactionLoading }, captureTransaction] =
    useAxios(
      {
        url: TRANSACTION_CAPTURE,
        method: 'get',
      },
      { manual: true },
    )

  useEffect(() => {
    transactionDataCall({
      params: {
        page: page,
        transaction_status: statusFilter,
      },
    })
  }, [])

  const [{ loading: downloadExcelLoading, response: downloadExcelResponse }, downloadExcelCall] =
    useAxios(
      {
        url: `${ANALYTICS_API}/transaction-analytics/download`,
        method: 'get',
        responseType: 'arraybuffer',
      },
      { manual: true },
    )

  useEffect(() => {
    setLoader(transactionDataLoading || captureTransactionLoading || downloadExcelLoading)
  }, [transactionDataLoading, captureTransactionLoading, downloadExcelLoading])

  useEffect(() => {
    if (transactionData && transactionData.success) {
      page === 1
        ? setTransactionList(transactionData.data)
        : setTransactionList((transactionList) => [...transactionList, ...transactionData.data])
    }
  }, [transactionData])

  useEffect(() => {
    if (captureTransactionData && captureTransactionData.success) {
      raiseToast('success', 'Captured successfully')
      setPage(1)
      transactionDataCall({
        params: {
          page: 1,
          transaction_status: statusFilter,
        },
      })
    }
  }, [captureTransactionData])

  useEffect(() => {
    if (downloadExcelResponse && downloadExcelResponse.status === 200) {
      const blob = new Blob([downloadExcelResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = `transaction-report.xlsx`
      link.click()
    }
  }, [downloadExcelResponse])

  const downloadExcel = () => {
    downloadExcelCall({
      params: {
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        transaction_status: statusFilter,
      },
    })
  }

  const applyFilter = () => {
    setPage(1)
    transactionDataCall({
      params: {
        page: 1,
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        transaction_status: statusFilter,
      },
    })
  }

  const resetFilters = () => {
    if (dateRangeRef.current) {
      dateRangeRef.current.$picker[0].value = ''
    }
    setPage(1)
    setStartDate('')
    setEndDate('')
    setSubscriptionFilter('')
    setStatusFilter('complete')
    transactionDataCall({
      params: {
        page: 1,
      },
    })
  }

  const handleCaptureTransaction = (info) => {
    captureTransaction({
      params: {
        tx_id: info.transaction_id,
      },
    })
  }

  const tableConfig = [
    {
      header: 'Transaction ID',
      dataCell: (info) => info.transaction_id,
      width: 17,
    },
    {
      header: 'Txn Date',
      dataCell: (info) => moment.utc(info.time).format('DD-MM-YYYY'),
      width: 10,
    },
    {
      header: 'User Email',
      dataCell: (info) => info.user_email,
      width: 13,
    },
    // {
    //   header: 'Transaction Amount',
    //   dataCell: (info) => info.transaction_amount,
    //   width: 9,
    // },
    {
      header: 'Status',
      dataCell: (info) => {
        switch (info.transaction_status) {
          case 'new':
            return 'Processing'
          case 'complete':
            return 'Completed'
          case 'failed':
            return 'Failed'
          case 'abandoned':
            return 'Abandoned'
          default:
            return ''
        }
      },
      width: 9,
    },
    {
      header: 'Plan',
      dataCell: (info) => info.subscription_plan,
      width: 11,
    },
    {
      header: 'Plan Amount',
      dataCell: (info) =>
        `${Number(info.subscription_amount).toFixed(2)}${
          info.currency ? getSymbolFromCurrency(info.currency) : ''
        }`,
      width: 10,
    },
    {
      header: 'Tax',
      dataCell: (info) =>
        `${Number(info.tax_amount).toFixed(2)}${
          info.currency ? getSymbolFromCurrency(info.currency) : ''
        }`,
      width: 8,
    },
    // {
    //   header: 'Coupon Applied',
    //   dataCell: (info) => info.artist_type,
    //   width: 10,
    // },
    {
      header: 'Discount',
      dataCell: (info) =>
        `${Number(info.discount_amount).toFixed(2)}${
          info.currency ? getSymbolFromCurrency(info.currency) : ''
        }`,
      width: 8,
    },
    {
      header: 'Total',
      dataCell: (info) =>
        `${Number(info.transaction_amount).toFixed(2)}${
          info.currency ? getSymbolFromCurrency(info.currency) : ''
        }`,
      width: 10,
    },
    {
      header: <FontAwesomeIcon icon={faAngleDoubleDown} size="lg" className="mx-1" />,
      dataCell: (info) => {
        return (
          <>
            {info.transaction_status === 'new' ? (
              <CIcon
                icon={cilDollar}
                style={{ cursor: 'pointer' }}
                size="lg"
                title="Capture"
                onClick={() => handleCaptureTransaction(info)}
              />
            ) : (
              <CIcon
                icon={cilInfo}
                style={{ cursor: 'pointer' }}
                size="lg"
                title="Edit"
                onClick={() => raiseToast('warn', 'work in progess')}
              />
            )}
          </>
        )
      },
      width: 9,
    },
  ]

  const handleDateChange = (event, picker) => {
    picker.element.val(
      picker.startDate.format('DD/MM/YYYY') + ' - ' + picker.endDate.format('DD/MM/YYYY'),
    )
    setStartDate(moment.utc(picker.startDate).format('YYYY-MM-DDTHH:mm:ss'))
    setEndDate(moment.utc(picker.endDate).format('YYYY-MM-DDTHH:mm:ss'))
  }

  const handleCancel = (event, picker) => {
    picker.element.val('')
    setStartDate('')
    setEndDate('')
  }

  const handleNextPage = () => {
    transactionDataCall({
      params: {
        page: page + 1,
        subscription_plan: subscriptionFilter,
        start_date: startDate,
        end_date: endDate,
        transaction_status: statusFilter,
      },
    })
    setPage((page) => page + 1)
  }
  return (
    <CCard>
      <div className="d-flex justify-content-between mt-3 py-2">
        <div className="d-flex">
          <p className="mt-1">Filters:</p>
          <div className="mx-2">
            <CFormSelect
              size="sm"
              placeholder="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="" disabled>
                Status
              </option>
              <option value={'complete'}>Completed</option>
              <option value={'new'}>Processing</option>
              <option value={'failed'}>Failed</option>
              <option value={'abandoned'}>Abandoned</option>
            </CFormSelect>
          </div>
          <div className="mx-3">
            <CFormSelect
              size="sm"
              placeholder="Subscription Plan"
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
            >
              <option value="" disabled>
                Subscription Plan
              </option>
              {!isEmpty(subscriptionData) &&
                subscriptionData.map((info, index) => (
                  <option value={info._id} key={index}>
                    {info.name}
                  </option>
                ))}
            </CFormSelect>
          </div>
          <div className="mx-3">
            <DateRangePicker
              ref={dateRangeRef}
              initialSettings={{
                autoUpdateInput: false,
                locale: { format: 'DD/MM/YYYY', cancelLabel: 'Clear' },
                placeholder: 'Select Date Range',
              }}
              onApply={handleDateChange}
              onCancel={handleCancel}
            >
              <CFormInput
                size="sm"
                type="text"
                className=""
                defaultValue=""
                placeholder="Select Date Range"
              />
            </DateRangePicker>
          </div>
          <div className="mx-2">
            <CButton color="info" size="sm" className="text-white" onClick={applyFilter}>
              Apply
            </CButton>
          </div>
          <p
            className="mt-1"
            style={{ color: '#1976d2', cursor: 'pointer' }}
            onClick={resetFilters}
          >
            Reset Filter
          </p>
        </div>
        <div className="d-flex">
          <div className="mx-2">
            <CButton color="success" size="sm" className="text-white" onClick={downloadExcel}>
              Download Excel
            </CButton>
          </div>
        </div>
      </div>
      <div style={{ zIndex: '0' }}>
        <SearchTable
          type={`Transaction Report`}
          tableCardHeight={63}
          disableButton={true}
          disableSearch={true}
          marginDisable={true}
          textAlign={'center'}
          tableContent={transactionList}
          tableConfig={tableConfig}
          hashWidth={4}
          handleNextPage={handleNextPage}
          paginationEnable={
            transactionData && !isEmpty(transactionData.data) && transactionData.total > page * 20
          }
          totalTableContent={transactionData?.total}
        />
      </div>
    </CCard>
  )
}

export default TransactionReport
